import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

/* ----------------------------------------------------------------
   Types & Data
   ---------------------------------------------------------------- */

export type FeatureQuota = {
  brandInsight: number;
  strategyPlan: number;
  imageGen: number;
  videoGen: number;
  viralMatch: number;
  videoRemix: number;
  tkSolution: number;
};

export const FEATURE_KEYS: (keyof FeatureQuota)[] = [
  'brandInsight',
  'strategyPlan',
  'imageGen',
  'videoGen',
  'viralMatch',
  'videoRemix',
  'tkSolution',
];

export type PricingLanguage = 'en' | 'zh';

export const PLAN_QUOTAS_BY_LANGUAGE: Record<PricingLanguage, Record<string, FeatureQuota>> = {
  en: {
    free: {
      brandInsight: 0,
      strategyPlan: 0,
      imageGen: 1,
      videoGen: 3,
      viralMatch: 0,
      videoRemix: 1,
      tkSolution: 0,
    },
    starter: {
      brandInsight: 3,
      strategyPlan: 3,
      imageGen: 57,
      videoGen: 180,
      viralMatch: 7,
      videoRemix: 68,
      tkSolution: 6,
    },
    basic: {
      brandInsight: 8,
      strategyPlan: 8,
      imageGen: 173,
      videoGen: 543,
      viralMatch: 20,
      videoRemix: 204,
      tkSolution: 19,
    },
    pro: {
      brandInsight: 27,
      strategyPlan: 27,
      imageGen: 577,
      videoGen: 1812,
      viralMatch: 68,
      videoRemix: 680,
      tkSolution: 62,
    },
  },
  zh: {
    free: {
      brandInsight: 0,
      strategyPlan: 0,
      imageGen: 1,
      videoGen: 3,
      viralMatch: 0,
      videoRemix: 1,
      tkSolution: 0,
    },
    starter: {
      brandInsight: 1,
      strategyPlan: 1,
      imageGen: 30,
      videoGen: 93,
      viralMatch: 4,
      videoRemix: 35,
      tkSolution: 3,
    },
    basic: {
      brandInsight: 8,
      strategyPlan: 8,
      imageGen: 170,
      videoGen: 533,
      viralMatch: 20,
      videoRemix: 200,
      tkSolution: 18,
    },
    pro: {
      brandInsight: 28,
      strategyPlan: 28,
      imageGen: 594,
      videoGen: 1867,
      viralMatch: 70,
      videoRemix: 700,
      tkSolution: 64,
    },
  },
};

export function getPlanQuotas(language: PricingLanguage): Record<string, FeatureQuota> {
  return PLAN_QUOTAS_BY_LANGUAGE[language] ?? PLAN_QUOTAS_BY_LANGUAGE.en;
}

/* ----------------------------------------------------------------
   Component
   ---------------------------------------------------------------- */

interface Props {
  planId: string;
  isEnterprise?: boolean;
  highlightDiffFrom?: FeatureQuota | null;
}

const FeatureQuotaTable: React.FC<Props> = ({ planId, isEnterprise }) => {
  const { t, language } = useLanguage();
  const quota = getPlanQuotas(language)[planId];

  return (
    <div className="space-y-1">
      <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
        {t('pricing.featureQuota.title')}
      </h4>

      <ul className="space-y-0.5">
        {FEATURE_KEYS.map((key) => {
          const value = isEnterprise ? -1 : (quota?.[key] ?? 0);
          const isUnlimited = value === -1;
          const isZero = value === 0;

          return (
            <li
              key={key}
              className="flex items-center justify-between py-1 px-2 rounded-md text-xs"
            >
              <span className={isZero ? 'text-muted-foreground/50' : ''}>
                {t(`pricing.feature.${key}`)}
              </span>

              <span
                className={`font-semibold tabular-nums ${
                  isUnlimited
                    ? 'text-primary'
                    : isZero
                      ? 'text-muted-foreground/40'
                      : 'text-foreground'
                }`}
              >
                {isUnlimited
                  ? t('pricing.quota.unlimited')
                  : value}
              </span>
            </li>
          );
        })}
      </ul>

      <p className="text-[11px] text-muted-foreground/60 pt-2">
        {t('pricing.featureQuota.footerNote')}
      </p>
    </div>
  );
};

export default FeatureQuotaTable;